import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { X, ChevronDown, Tag, Monitor, Smartphone, Eye } from 'lucide-react';
import { getTags } from '../../actions/tagActions';
import useQuillImageResize, { IMAGE_SIZE_FORMATS } from '../lessons/useQuillImageResize';
import RichTextRenderer from '../common/RichTextRenderer';

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'align',
  'blockquote', 'code-block',
  'link', 'image', 'video',
  'actionButton',
  // keeps a resized image's width alive across a save/reload cycle
  ...IMAGE_SIZE_FORMATS
];

const LessonForm = ({ mode = 'new', lesson = null, onSubmit, onClose, tag: { tags }, getTags }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [mobileContent, setMobileContent] = useState('');
  const [experiencePoints, setExperiencePoints] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [contentTab, setContentTab] = useState('desktop'); // 'desktop' | 'mobile'
  const [previewMode, setPreviewMode] = useState(null); // null | 'desktop' | 'mobile'
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const quillRef = useRef(null);
  const mobileQuillRef = useRef(null);

  useEffect(() => {
    getTags();
  }, [getTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const initializeForm = async () => {
      setIsLoading(true);
      try {
        if (lesson) {
          setTitle(lesson.title || '');
          setContent(lesson.content || '');
          setMobileContent(lesson.mobileContent || '');
          setExperiencePoints(lesson.experiencePoints || 0);
          setSelectedTags(lesson.tags ? lesson.tags.map(tag => tag._id) : []);
        } else {
          setTitle('');
          setContent('');
          setMobileContent('');
          setExperiencePoints(0);
          setSelectedTags([]);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeForm();
  }, [lesson]);

  const isView = mode === 'view';

  // Click an image in the editor to resize it (presets + drag handle)
  useQuillImageResize(quillRef, setContent, !isLoading && !isView);
  useQuillImageResize(mobileQuillRef, setMobileContent, !isLoading && !isView);
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    if (onSubmit) onSubmit({ title, content, mobileContent: isQuillEmpty(mobileContent) ? null : mobileContent, experiencePoints, tags: selectedTags });
    if (isNew) {
      setTitle('');
      setContent('');
      setMobileContent('');
      setExperiencePoints(0);
      setSelectedTags([]);
    }
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(id => id !== tagId));
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return tags.filter(tag => 
      !selectedTags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Quill leaves behind <p><br></p> when editor is "empty" — treat that as null
  const isQuillEmpty = (html) => {
    if (!html) return true;
    const stripped = html.replace(/<[^>]*>/g, '').trim();
    return stripped.length === 0;
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-lg w-full max-w-4xl mx-4 p-6 border-2 border-[#1086a4]"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-[#054581]">
            {isNew && 'Add New Lesson'}
            {isEdit && 'Edit Lesson'}
            {isView && 'View Lesson'}
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-[#1086a4] focus:outline-none transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-[#054581] mb-2">
                Lesson Title
              </label>
              <input
                type="text"
                id="title"
                placeholder="Enter lesson title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                required
                disabled={isView}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#054581]">
                  Lesson Content
                </label>
                <div className="flex items-center gap-2">
                  {/* Preview toggle */}
                  <button
                    type="button"
                    onClick={() => setPreviewMode(previewMode ? null : 'desktop')}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                      previewMode ? 'bg-[#1086a4] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Eye className="w-3 h-3" />
                    Preview
                  </button>
                  {previewMode && (
                    <div className="flex border border-gray-300 rounded overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setPreviewMode('desktop')}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-medium transition-colors ${
                          previewMode === 'desktop' ? 'bg-[#054581] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Monitor className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewMode('mobile')}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-medium transition-colors ${
                          previewMode === 'mobile' ? 'bg-[#054581] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Smartphone className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview panel */}
              {previewMode && (
                <div className="mb-3 border border-gray-200 rounded-lg bg-gray-50 p-4">
                  <div className="flex items-center justify-center">
                    <div
                      className={`bg-white border border-gray-300 rounded-lg overflow-auto ${
                        previewMode === 'mobile' ? 'w-[375px] h-[667px]' : 'w-full h-[500px]'
                      }`}
                      style={previewMode === 'mobile' ? { boxShadow: '0 0 0 8px #1F2D3D, 0 0 0 10px #374151' } : {}}
                    >
                      <div className="p-4">
                        <RichTextRenderer
                          content={previewMode === 'mobile' && mobileContent ? mobileContent : content}
                          className="lesson-content"
                          fontSize="17px"
                          lineHeight="1.7"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-xs text-gray-400 mt-2">
                    {previewMode === 'mobile'
                      ? (mobileContent ? 'Showing mobile content' : 'No mobile content — showing desktop content')
                      : 'Desktop preview'}
                  </p>
                </div>
              )}

              {/* Desktop / Mobile tabs */}
              <div className="flex border-b border-gray-200 mb-0">
                <button
                  type="button"
                  onClick={() => setContentTab('desktop')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    contentTab === 'desktop'
                      ? 'border-[#1086a4] text-[#1086a4]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setContentTab('mobile')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    contentTab === 'mobile'
                      ? 'border-[#1086a4] text-[#1086a4]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile
                  {!mobileContent && <span className="text-xs text-gray-400 ml-1">(optional)</span>}
                </button>
              </div>

              {/* Desktop editor */}
              <div style={{ display: contentTab === 'desktop' ? 'block' : 'none' }}>
                <div className="border border-[#1086a4] border-t-0 rounded-b-md overflow-hidden" style={{ height: '500px' }}>
                  <ReactQuill
                    ref={quillRef}
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-[#f9fafb] h-full flex flex-col"
                    readOnly={isView}
                    style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  />
                </div>
              </div>

              {/* Mobile editor */}
              <div style={{ display: contentTab === 'mobile' ? 'block' : 'none' }}>
                <div className="border border-[#1086a4] border-t-0 rounded-b-md overflow-hidden" style={{ height: '500px' }}>
                  <ReactQuill
                    ref={mobileQuillRef}
                    value={mobileContent}
                    onChange={setMobileContent}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-[#f9fafb] h-full flex flex-col"
                    readOnly={isView}
                    placeholder="Optional: Add mobile-specific content. If left empty, desktop content will be used on all devices."
                    style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  />
                </div>
                {!mobileContent && (
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to use desktop content on mobile devices. Only add content here if the desktop version doesn't display well on small screens.
                  </p>
                )}
              </div>

              <style>
                {`
                  .ql-container {
                    flex: 1;
                    overflow: auto;
                    height: auto !important;
                  }
                  .ql-toolbar {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    background: #f9fafb;
                  }
                `}
              </style>
            </div>

            <div>
              <label htmlFor="experiencePoints" className="block text-sm font-medium text-[#054581] mb-2">
                Experience Points
              </label>
              <input
                type="number"
                id="experiencePoints"
                min="0"
                max="1000"
                value={experiencePoints}
                onChange={e => setExperiencePoints(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                placeholder="0"
                disabled={isView}
              />
              <span className="text-xs text-gray-500 mt-1">
                Points awarded when lesson is completed (default: 0)
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#054581] mb-2">
                Tags
              </label>
              {!isView && (
                <div className="relative" ref={tagDropdownRef}>
                  <div
                    onClick={handleTagInputClick}
                    className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
                  >
                    <div className="flex flex-wrap items-center gap-1">
                      {getSelectedTagObjects().map(tag => (
                        <span
                          key={tag._id}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                        >
                          <Tag className="w-3 h-3 mr-1" />
                          {tag.title}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTag(tag._id);
                            }}
                            className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={tagSearchTerm}
                        onChange={(e) => setTagSearchTerm(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        onFocus={() => setShowTagDropdown(true)}
                        placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search tags..."}
                        className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                      />
                    </div>
                  </div>
                  {showTagDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                      {getFilteredTags().length > 0 ? (
                        getFilteredTags().map(tag => (
                          <button
                            key={tag._id}
                            type="button"
                            onClick={() => toggleTag(tag._id)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                          >
                            <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                            {tag.title}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isView && getSelectedTagObjects().length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {getSelectedTagObjects().map(tag => (
                    <span
                      key={tag._id}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {tag.title}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                Cancel
              </button>
              {!isView && (
                <button
                  type="submit"
                  className="flex-1 py-2 px-4 bg-[#1086a4] text-white rounded-md font-medium hover:bg-[#054581] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
                >
                  {isNew ? 'Add Lesson' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

const mapStateToProps = state => ({
  tag: state.tag
});

export default connect(mapStateToProps, { getTags })(LessonForm);
